import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  ChartBarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MinusCircleIcon,
} from '@heroicons/react/24/outline';
import { createGconSession } from '@/api/gcon';
import FileDropZone from './FileDropZone';
import { byName, EXAMPLE_YEAR, planGconUpload } from '@/utils/gconUtils';

const FileCell = ({ files, missingLabel }) =>
  files.length ? (
    <span className="flex items-center gap-1.5 text-gray-700 min-w-0">
      <CheckCircleIcon className="w-4 h-4 text-teal-500 flex-shrink-0" />
      <span className="truncate" title={files.map(f => f.name).join(', ')}>
        {files.map(f => f.name).join(', ')}
      </span>
    </span>
  ) : (
    <span className="flex items-center gap-1.5 text-gray-400">
      <MinusCircleIcon className="w-4 h-4 flex-shrink-0" />
      {missingLabel}
    </span>
  );

FileCell.propTypes = {
  files: PropTypes.arrayOf(PropTypes.instanceOf(File)).isRequired,
  missingLabel: PropTypes.string.isRequired,
};

/** Upload one GCON Input/Output pair per year; the analysis is saved as a session. */
const UploadForm = ({ onCreated }) => {
  const [inputFiles, setInputFiles] = useState([]);
  const [outputFiles, setOutputFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const addFiles = setter => added => setter(prev => [...prev, ...added].sort(byName));
  const removeFile = setter => i => setter(prev => prev.filter((_, j) => j !== i));

  const { rows, singlePair, problem, outputOnlyYears } = planGconUpload(inputFiles, outputFiles);
  const yearCount = rows.filter(r => r.year).length;
  const canAnalyze = outputFiles.length > 0 && !problem && !submitting;

  const handleAnalyze = async () => {
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const { data } = await createGconSession(inputFiles, outputFiles);
      onCreated(data);
    } catch (err) {
      const msgs = err?.response?.data?.non_field_errors ?? err?.response?.data?.detail ?? [err.message];
      setErrorMsg(Array.isArray(msgs) ? msgs.join(' ') : String(msgs));
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm w-full max-w-2xl p-8">
      <div className="mb-5">
        <h1 className="text-xl font-bold text-gray-800 mb-1">GCON Hot Spot Analysis</h1>
        <p className="text-sm text-gray-500">
          Identify emission hot spots — sources with the highest CO₂e and greatest month-to-month variability — across
          one or more reporting years.
        </p>
      </div>

      {/* How multi-year uploads work, up front rather than discovered by trial and error */}
      <ol className="bg-teal-50 border border-teal-100 rounded-lg px-4 py-3 mb-6 text-sm text-gray-700 space-y-1.5">
        {[
          <>
            For <strong>each year</strong>, add that year&apos;s <strong>GCON Output</strong> and, if you have it, its{' '}
            <strong>GCON Input</strong>. Without the Input, production comes from the Output&apos;s Intensity sheet and
            Levelization isn&apos;t available for that year.
          </>,
          <>
            Start each file name with its year, e.g.{' '}
            <code className="text-xs bg-white px-1 rounded">{EXAMPLE_YEAR} Gcon Input.xlsx</code> and{' '}
            <code className="text-xs bg-white px-1 rounded">{EXAMPLE_YEAR} Gcon Output.xlsx</code>.
          </>,
          <>
            Add <strong>as many years as you like</strong> — they&apos;re analysed together so you can compare them. You
            can select several files at once.
          </>,
        ].map((step, i) => (
          <li key={i} className="flex gap-2">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-teal-500 text-white text-xs font-semibold flex items-center justify-center mt-px">
              {i + 1}
            </span>
            <span>{step}</span>
          </li>
        ))}
      </ol>

      <div className="grid grid-cols-2 gap-6 mb-5">
        <FileDropZone
          id="gcon-input"
          label="GCON Input files"
          description="One per year · raw activity data (fuel usage, electricity, etc.)"
          files={inputFiles}
          onFiles={addFiles(setInputFiles)}
          onRemove={removeFile(setInputFiles)}
        />
        <FileDropZone
          id="gcon-output"
          label="GCON Output files"
          description="One per year · computed GHG emissions per source per month"
          files={outputFiles}
          onFiles={addFiles(setOutputFiles)}
          onRemove={removeFile(setOutputFiles)}
        />
      </div>

      {/* Year-by-year checklist so it's obvious what's covered and what's missing */}
      {rows.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-hidden mb-5">
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Years in this upload</p>
            <p className="text-xs text-gray-400">
              {yearCount > 0 ? `${yearCount} year${yearCount === 1 ? '' : 's'}` : ''}
            </p>
          </div>
          <table className="w-full text-xs table-fixed">
            <thead className="text-gray-500">
              <tr>
                <th className="text-left font-medium px-4 py-2 w-20">Year</th>
                <th className="text-left font-medium px-2 py-2">Input</th>
                <th className="text-left font-medium px-2 py-2">Output</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.year ?? 'unknown'} className="border-t border-gray-100">
                  <td className="px-4 py-2 font-semibold text-gray-700">
                    {r.year ?? (singlePair ? 'From file' : 'No year')}
                  </td>
                  <td className="px-2 py-2">
                    <FileCell
                      files={r.input}
                      missingLabel={r.output.length && r.year ? 'None · uses Intensity sheet' : 'Add Input'}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <FileCell files={r.output} missingLabel="Add Output" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {outputOnlyYears.length > 0 && !problem && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex gap-2">
          <InformationCircleIcon className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            No Input file for {outputOnlyYears.join(', ')}. Production for{' '}
            {outputOnlyYears.length === 1 ? 'that year' : 'those years'} will come from the Output&apos;s Intensity
            sheet (totals only, no C1–C4 split), and Levelization won&apos;t include those months. Add the Input if you
            have it.
          </p>
        </div>
      )}

      {(problem || errorMsg) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex gap-2">
          <ExclamationTriangleIcon className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{problem ?? errorMsg}</p>
        </div>
      )}

      <button type="button" onClick={handleAnalyze} disabled={!canAnalyze} className="btn btn-primary w-full">
        {submitting ? (
          <>
            <span className="loading loading-spinner loading-sm" />
            Analyzing…
          </>
        ) : (
          <>
            <ChartBarIcon className="w-4 h-4" />
            {yearCount > 1 ? `Analyze ${yearCount} years and save` : 'Analyze and save'}
          </>
        )}
      </button>
    </div>
  );
};

UploadForm.propTypes = {
  onCreated: PropTypes.func.isRequired,
};

export default UploadForm;
